// Enhanced POIList.js with indicators for missing fields
import React, { useState } from 'react';
import JSZip from 'jszip';
import { GripVertical, Edit, MinusCircle, Lock, AlertTriangle, CheckCircle, Download, Loader, RefreshCw } from 'lucide-react'; // Added Loader and RefreshCw
import { generateQRCodePngBlob, QRCodeGenerator } from '../ItineraryCreator/ui/QRCodeGenerator';
import FeatureGuard from '../FeatureGuard';

export const POIList = ({
  points,
  onEdit,
  onRemove,
  onReorder,
  isTranslatingAll,
  translatingFields,
  checkPointIdFormat,
  regeneratePointIds,
  itineraryMetadata
}) => { // Added new props for ID validation and regeneration
  const [isBuildingQrZip, setIsBuildingQrZip] = useState(false);
  const [qrZipStatus, setQrZipStatus] = useState('');
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    onReorder(dragIndex, dropIndex);
  };

  // Function to get tier badge styling
  const getTierBadge = (tier) => {
    switch(tier) {
      case 'premium':
        return <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center"><Lock className="w-3 h-3 mr-1" />Premium</span>;
      case 'platinum':
        return <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center"><Lock className="w-3 h-3 mr-1" />Platinum</span>;
      case 'free':
      default:
        return <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Free</span>;
    }
  };

  // Function to truncate long text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Function to check if a POI is complete
  const checkCompletion = (point) => {
    const missingFields = [];

    // Check essential fields
    if (!point.inDepthDescription) missingFields.push('In-Depth Description');
    if (!point.audioUrl) missingFields.push('Audio Narration');
    if (!point.photoUrl) missingFields.push('Photo');

    // Check coordinates
    if (!point.coordinates.latitude || !point.coordinates.longitude) {
      missingFields.push('Coordinates');
    }

    return missingFields;
  };

  // Check if any point IDs are not in the correct format
  const hasInvalidPointIds = points.some(point => !checkPointIdFormat(point.id, itineraryMetadata));
  const guideQrCode = itineraryMetadata?.parent_guide_id || itineraryMetadata?.id || '';
  const guideQrUrl = guideQrCode ? `https://guidesofia.com/qr/${guideQrCode}` : '';
  const guideQrLabel = itineraryMetadata?.name || 'Guide';

  const safeFilename = (value, fallback) => String(value || fallback)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || fallback;

  const downloadAllQrCodes = async () => {
    if (isBuildingQrZip || !guideQrUrl) return;
    setIsBuildingQrZip(true);
    setQrZipStatus('Preparing the Whole Guide QR code...');
    try {
      const zip = new JSZip();
      const qrItems = [
        { value: guideQrUrl, name: guideQrLabel, filename: `00_whole_guide_${safeFilename(guideQrLabel, 'guide')}` },
        ...points.filter((point) => point?.id).map((point, index) => ({
          value: `https://guidesofia.com/qr/${point.id}`,
          name: point.name || `Stop ${index + 1}`,
          filename: `${String(index + 1).padStart(2, '0')}_stop_${safeFilename(point.name, `stop_${index + 1}`)}`,
        })),
      ];
      for (let index = 0; index < qrItems.length; index += 1) {
        const item = qrItems[index];
        setQrZipStatus(`Creating QR code ${index + 1} of ${qrItems.length}: ${item.name}`);
        const blob = await generateQRCodePngBlob({ qrValue: item.value, withHeader: true });
        zip.file(`${item.filename}_audio_guide.png`, blob);
      }
      setQrZipStatus('Building ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${safeFilename(guideQrLabel, 'guide')}_all_qr_codes.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
      setQrZipStatus(`${qrItems.length} QR codes downloaded.`);
    } catch (error) {
      console.error('Could not build the QR code ZIP:', error);
      setQrZipStatus(error.message || 'The QR code ZIP could not be created.');
    } finally {
      setIsBuildingQrZip(false);
    }
  };
  
  return (
    <div className="border-t pt-6">
      {guideQrUrl && (
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-cyan-200 bg-cyan-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold text-cyan-950">All guide QR codes</div>
            <p className="text-sm text-cyan-800">Download the Whole Guide QR and all {points.length} stop QR codes with the Audio-Guide header.</p>
            {qrZipStatus && <p className="mt-1 text-xs font-medium text-cyan-700">{qrZipStatus}</p>}
          </div>
          <button type="button" onClick={downloadAllQrCodes} disabled={isBuildingQrZip} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800 disabled:bg-slate-400">
            {isBuildingQrZip ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isBuildingQrZip ? 'Creating QR ZIP...' : 'Download all QR codes (ZIP)'}
          </button>
        </div>
      )}
      
      {/* Warning for invalid point IDs */}
      {hasInvalidPointIds && points.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">Warning: Point IDs are not in the correct format!</span>
          </div>
          <p className="text-sm text-red-600 mb-3">
            Point IDs should be in the format: <strong>{itineraryMetadata.parent_guide_id || itineraryMetadata.id}_stop_sequence_{(itineraryMetadata.language || 'en').toLowerCase().split(/[-_]/)[0]}</strong>
            <br/>Example: {itineraryMetadata.parent_guide_id || itineraryMetadata.id}_1_{(itineraryMetadata.language || 'en').toLowerCase().split(/[-_]/)[0]}
          </p>
          <FeatureGuard featureName="regeneratePointIds" requiredAccess="edit">
            <button
              onClick={regeneratePointIds}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate All Point IDs
            </button>
          </FeatureGuard>
        </div>
      )}

      {guideQrUrl && (
        <div className="mb-4 bg-teal-50 border border-teal-200 p-4 rounded-lg flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-teal-900">Guide QR Code</div>
            <p className="text-sm text-teal-700">
              Generate one QR code for the full guide instead of a single stop.
            </p>
          </div>
          <QRCodeGenerator
            qrValue={guideQrUrl}
            stopName={guideQrLabel}
            className="px-3 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 flex items-center justify-center gap-2 shrink-0"
          >
            <span>Whole Guide QR</span>
          </QRCodeGenerator>
        </div>
      )}
      
      <div className="space-y-4">
        {points.map((point, index) => {
          const missingFields = checkCompletion(point);
          const isComplete = missingFields.length === 0;
          const isTranslating = isTranslatingAll || translatingFields[point.id]; // Check if this specific POI is translating

          return (
            <div
              key={point.id}
              className={`${isComplete ? 'bg-gray-50' : 'bg-red-100 border-2 border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.12)]'} p-4 rounded-lg ${isTranslating ? 'opacity-60 pointer-events-none' : ''}`}
              draggable={!isTranslating}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="flex gap-4">
                {/* Left side - Information */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <button className="cursor-move" disabled={isTranslating}>
                      <GripVertical className="w-5 h-5 text-gray-400" />
                    </button>
                    <div className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold flex items-center">
                        {point.name}
                        {getTierBadge(point.tier || 'free')}
                        {isTranslating ? (
                          <span className="ml-2 text-blue-500 flex items-center">
                            <Loader className="w-4 h-4 animate-spin" />
                          </span>
                        ) : isComplete ? (
                          <span className="ml-2 text-green-500 flex items-center">
                            <CheckCircle className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="ml-2 text-red-500 flex items-center">
                            <AlertTriangle className="w-4 h-4" />
                          </span>
                        )}
                      </h4>
                      <div className="text-xs text-gray-500 font-mono">
                        ID: {point.id}
                      </div>
                      {!isComplete && !isTranslating && (
                        <span className="text-xs text-red-600">
                          Missing: {missingFields.slice(0, 3).join(', ')}
                          {missingFields.length > 3 ? ` and ${missingFields.length - 3} more` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* All text content moved here */}
                  <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                    <div>
                      <span className="font-medium">Category:</span> {point.category}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span> {point.estimatedDuration} minutes
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium">Description:</span> {point.description}
                    </div>
                    {point.inDepthDescription ? (
                      <div className="col-span-2">
                        <span className="font-medium">In-Depth Description:</span> {truncateText(point.inDepthDescription, 200)}
                      </div>
                    ) : (
                      <div className="col-span-2 text-amber-600 bg-amber-50 p-2 rounded flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        <span>In-Depth Description is missing</span>
                      </div>
                    )}

                    {/* Completion Indicators */}
                    <div className="col-span-2 mt-2">
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${point.photoUrl ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.photoUrl ? '✓ Photo' : '✗ Photo Missing'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${point.sphericalPhotoUrl ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.sphericalPhotoUrl ? '✓ Spherical' : '✗ Spherical Missing'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${point.audioUrl ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.audioUrl ? '✓ Audio' : '✗ Audio Missing'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${point.historicalContext ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.historicalContext ? '✓ Historical' : '✗ Historical Missing'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${point.culturalSignificance ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.culturalSignificance ? '✓ Cultural' : '✗ Cultural Missing'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${point.hiddenGems ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {point.hiddenGems ? '✓ Hidden Gems' : '✗ Hidden Gems Missing'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side - Buttons and Image */}
                <div className="w-48 flex flex-col">
                  {/* Buttons at top */}
                  <div className="flex flex-col gap-2 mb-3">
                    <QRCodeGenerator
                      qrValue={`https://guidesofia.com/qr/${point.id}`}
                      stopName={point.name}
                      className={`px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 flex items-center justify-center gap-1 ${isTranslating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span>QR Code</span>
                    </QRCodeGenerator>
                    <button
                      onClick={() => onEdit(point)}
                      className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isTranslating}
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit POI</span>
                    </button>
                    <button
                      onClick={() => onRemove(point.id)}
                      className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isTranslating}
                    >
                      <MinusCircle className="w-3 h-3" />
                      <span>Delete</span>
                    </button>
                  </div>

                  {/* Square Image at bottom */}
                  {point.photoUrl && (
                    <div className="w-full aspect-square">
                      <img
                        src={point.photoUrl}
                        alt={point.name}
                        className="w-full h-full object-cover rounded-lg shadow-sm"
                        onError={(e) => {
                          console.log('Image failed to load:', point.photoUrl);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', point.photoUrl);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default POIList;
